import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IntersectionOptions, useInView } from 'react-intersection-observer';
import AVATAR_DEFAULT from '@/assets/register-doctor-default-avatar.png';

import './index.scss';

interface IProps {
  url: string;
  className?: string;
  defaultImg?: string;
  onInview?: () => void;
  onLoadError?: () => void;
  isKcdnImg?: boolean; // 如果是kcdn图片可以开启图片服务
  altStr?: string;
  onClick?: () => void;
  useInViewOptions?: IntersectionOptions;
  needHasInView?: boolean;
}

const WebImage = (props: IProps) => {
  const {
    url,
    className,
    defaultImg = AVATAR_DEFAULT,
    onInview,
    onLoadError,
    isKcdnImg = true,
    altStr = '',
    onClick,
    useInViewOptions,
    needHasInView
  } = props;

  const [isError, setIsError] = useState(false);
  const [hasInView, setHasInView] = useState(false);

  const imgRef = useRef<HTMLImageElement>({} as HTMLImageElement);

  const { ref, inView } = useInView(
    useInViewOptions || {
      threshold: 0,
      triggerOnce: true
    }
  );

  useEffect(() => {
    if (inView) {
      setHasInView(true);
      // 用于元素曝光等
      onInview && onInview();
    }
  }, [inView]);

  const handleError = (error) => {
    setIsError(true);
    onLoadError && onLoadError();
  };

  const getCdnUrl = (imgUrl: string) => {
    if (!imgUrl) {
      return defaultImg;
    }
    if (!isKcdnImg || imgUrl.includes(`base64`)) {
      return imgUrl;
    }
    const { width } = imgRef?.current?.getBoundingClientRect();
    if (imgUrl.includes('x-oss-process=image/resize,w_')) {
      return imgUrl.replace(
        /x-oss-process=image\/resize,w_[0-9.]*/gm,
        `x-oss-process=image/resize,w_${Math.floor(
          width * window.devicePixelRatio
        )}`
      );
    }
    return `${imgUrl}?x-oss-process=image/resize,w_${Math.floor(
      width * window.devicePixelRatio
    )}`;
  };

  // Use `useCallback` so we don't recreate the function on each render - Could result in infinite loop
  const setRefs = useCallback(
    (node) => {
      // Ref's from useRef needs to have the node assigned to `current`
      imgRef.current = node;
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      ref(node);
    },
    [ref]
  );

  const handleClick = () => {
    onClick && onClick();
  };

  return (
    <img
      aria-hidden
      ref={setRefs}
      className={`ks-health-com-image ${className ?? ''}`}
      src={
        inView && !isError
          ? getCdnUrl(url)
          : needHasInView
          ? hasInView
            ? getCdnUrl(url)
            : defaultImg
          : defaultImg
      }
      alt={altStr}
      onError={handleError}
      onClick={handleClick}
    />
  );
};

export default WebImage;
